import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Map, Pencil, Plus, Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Pagination, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { Can } from '@/app/guards/RoleGuard';
import { routesApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { RouteFormModal } from '../components/RouteFormModal';
import { RouteMappingModal } from '../components/RouteMappingModal';
import type { Route } from '@/domain/types';

export function RoutesPage(): JSX.Element {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Route | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [mappingTarget, setMappingTarget] = useState<Route | null>(null);

  const params = { page, pageSize: 25, isActive: 'all' as const };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.routes.list(params),
    queryFn: () => routesApi.list(params),
    placeholderData: (previous) => previous,
  });

  // Keep the open mapping modal in step with refetched data, so the agent and
  // shop count it shows update after a change rather than going stale.
  const activeMapping = mappingTarget
    ? (data?.rows.find((route) => route.id === mappingTarget.id) ?? mappingTarget)
    : null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('route.title')}
        description={t('route.mappingHelp')}
        actions={
          <Can permission="routes:create">
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t('route.new')}
            </Button>
          </Can>
        }
      />

      <Card>
        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={<Map className="h-6 w-6" />} title={t('route.empty')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('route.name')}</TH>
                  <TH>{t('route.agent')}</TH>
                  <TH align="center">{t('route.shopCount')}</TH>
                  <TH align="center">{t('common.status')}</TH>
                  <TH align="right">{t('common.actions')}</TH>
                </TR>
              </THead>

              <TBody>
                {data.rows.map((route) => (
                  <TR key={route.id}>
                    <TD className="font-medium text-slate-900">
                      {route.name}
                      {route.description && (
                        <span className="block text-xs font-normal text-slate-500">
                          {route.description}
                        </span>
                      )}
                    </TD>
                    <TD>
                      {route.agentName ? (
                        <span className="text-slate-700">{route.agentName}</span>
                      ) : (
                        // No agent means nobody collects this route's invoices.
                        <Badge tone="warning">{t('route.noAgent')}</Badge>
                      )}
                    </TD>
                    <TD align="center" numeric>
                      {route.shopCount}
                    </TD>
                    <TD align="center">
                      <Badge tone={route.isActive ? 'success' : 'neutral'}>
                        {route.isActive ? t('common.active') : t('common.inactive')}
                      </Badge>
                    </TD>
                    <TD align="right">
                      <Can permission="routes:update">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMappingTarget(route)}
                            leftIcon={<Settings2 className="h-4 w-4" />}
                          >
                            {t('nav.masterData')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(route);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </Can>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <RouteFormModal isOpen={isFormOpen} onClose={() => setFormOpen(false)} route={editing} />
      <RouteMappingModal
        isOpen={mappingTarget !== null}
        onClose={() => setMappingTarget(null)}
        route={activeMapping}
      />
    </div>
  );
}
